import { useEffect } from "react";
import Head from "next/head";
import Image from "next/image";

import { CircleCheckBig } from "lucide-react";
import { Wizard } from "react-use-wizard";

import NavbarLegacy from "@/components/marketing/pieces/NavbarLegacy";
import { QRFormLayout } from "@/components/qr/layout";
import { STEPS } from "@/lib/qr/constants";
import Footer from "../components/marketing/pieces/Footer";
import useQR from "../lib/stores/qr";

export default function GoogleBusinessProfileQRCodeGenerator() {
  const { setState } = useQR();

  useEffect(() => {
    setState("type", "profile");
  }, [setState]);

  return (
    <>
      <Head>
        <title>QR Code Generator for Google Business Profile | Rebolt</title>
        <meta
          name="description"
          content="Connect your physical business to your online presence with a Google Business Profile QR Code Generator, enhancing customer engagement and visibility."
        />

        <meta
          property="og:image"
          content="https://api.screenshotone.com/take?access_key=cQWOH7FyQTatDw&url=https://rebolthq.com/gbp-qr-code-generator&full_page=false&viewport_width=1200&viewport_height=850&device_scale_factor=1&format=jpg&image_quality=80&cache=true&cache_ttl=304800"
        />
      </Head>
      <NavbarLegacy />
      <QRFormLayout>
        <div className="flex flex-col items-center text-center pb-48 pt-28 md:bg-center md:bg-no-repeat md:bg-size-[95%]">
          <h1
            className="text-ash-900 font-bold text-2xl sm:text-3xl md:text-4xl mb-16"
            id="google-business-profile-qr-code-generator"
            tabIndex={-1}
          >
            Google Business Profile QR Code Generator
          </h1>
          <Wizard>
            {STEPS.map((step) => {
              const Component = step.component;
              return (
                <div key={step.stepKey} className="w-80 bg-white p-8 rounded-xl border-8 border-ash-200 md:w-[480px]">
                  <Component />
                </div>
              );
            })}
          </Wizard>
        </div>
      </QRFormLayout>

      <div className="bg-white">
        <div className="mx-auto max-w-3xl px-6 py-16 lg:px-8">
          <p className="text-xl leading-8">
            Easily connect your physical business with your online presence using the{" "}
            <strong>
              <a
                href="https://support.google.com/business/thread/216432089/how-do-i-create-a-qr-code-for-my-listing?hl=en"
                target="_blank"
                className="text-rebolt-700"
              >
                Google Business Profile QR Code Generator
              </a>
            </strong>
            . This tool helps clients access your{" "}
            <a href="https://rebolthq.com/tutorials/how-to-find-google-business-profile-id" className="text-rebolt-700">
              Google Business Profile
            </a>{" "}
            by simply scanning a QR code, allowing them to view your services, hours, location, and reviews instantly.
            Perfect for{" "}
            <a href="https://rebolthq.com/resources" className="text-rebolt-700">
              home service businesses
            </a>
            , it simplifies customer interaction and boosts online engagement.
          </p>
        </div>
      </div>

      <div className="bg-ash-100">
        <div className="mx-auto max-w-3xl px-6 py-16 lg:px-8">
          <h3 className="text-2xl font-bold tracking-tight text-ash-900" id="how-it-works" tabIndex={-1}>
            How It Works:
          </h3>
          <ol className="mt-8 list-decimal space-y-8 pl-6">
            <li className="flex gap-x-3">
              <CircleCheckBig aria-hidden="true" className="mt-1 h-5 w-5 flex-none text-rebolt-600" />
              <span>
                <strong className="font-semibold text-ash-900">Enter your business name</strong> to find your profile.
              </span>
            </li>
            <li className="flex gap-x-3">
              <CircleCheckBig aria-hidden="true" className="mt-1 h-5 w-5 flex-none text-rebolt-600" />
              <span>
                <strong className="font-semibold text-ash-900">Select your profile</strong> from the suggestions.
              </span>
            </li>
            <li className="flex gap-x-3">
              <CircleCheckBig aria-hidden="true" className="mt-1 h-5 w-5 flex-none text-rebolt-600" />
              <span>
                <strong className="font-semibold text-ash-900">Generate a QR code</strong> in seconds.
              </span>
            </li>
            <li className="flex gap-x-3">
              <CircleCheckBig aria-hidden="true" className="mt-1 h-5 w-5 flex-none text-rebolt-600" />
              <span>
                <strong className="font-semibold text-ash-900">Download and share</strong> it in various formats (PNG,
                SVG, PDF).
              </span>
            </li>
          </ol>
        </div>
      </div>

      <div className="bg-white">
        <div className="mx-auto max-w-3xl px-6 py-16 lg:px-8">
          <h3 className="text-2xl font-bold tracking-tight text-ash-900" id="benefits" tabIndex={-1}>
            Benefits:
          </h3>
          <ul className="mt-8 space-y-8 pl-6">
            <li className="flex flex-col  gap-3">
              <div className="flex items-start flex-col md:flex-row gap-1">
                <strong className="text-ash-900">Simplify Customer Interaction:</strong>
                <span>Customers can leave reviews, check details, or book services with one scan.</span>
              </div>
            </li>
            <li className="flex flex-col gap-3">
              <div className="flex items-start flex-col md:flex-row gap-1">
                <strong className="text-ash-900">Boost Marketing:</strong>
                <span>Add QR codes to vehicles, invoices, or emails for increased visibility.</span>
              </div>
            </li>
            <li className="flex flex-col gap-3">
              <div className="flex items-start flex-col md:flex-row gap-1">
                <strong className="text-ash-900">Track Performance:</strong>
                <span>Use analytics to measure scans and engagement.</span>
              </div>
            </li>
          </ul>
          <p className="mt-6 text-xl leading-8">
            For a focused approach to reviews, try the{" "}
            <a href="https://rebolthq.com/google-reviews-qr-code-creator" className="text-rebolt-700">
              Google Reviews QR Code Creator
            </a>
            . Both tools are great for improving local SEO and customer trust.
          </p>
        </div>
      </div>

      <div className="bg-ash-100">
        <div className="mx-auto max-w-3xl px-6 py-16 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight text-ash-900" id="using-our-qr-code-generator" tabIndex={-1}>
            Using Our QR Code Generator
          </h2>

          <h3
            className="mt-10 text-xl font-bold tracking-tight text-ash-900"
            id="1-enter-your-business-name"
            tabIndex={-1}
          >
            1. Enter Your Business Name
          </h3>
          <p className="mt-4">
            Start by typing your business name into the tool. As you type, it will automatically display matching Google
            Business Profiles for you to choose from.
          </p>

          <h3 className="mt-10 text-xl font-bold tracking-tight text-ash-900" id="2-select-your-profile" tabIndex={-1}>
            2. Select Your Google Business Profile
          </h3>
          <p className="mt-4">
            Pick your profile from the list. This step is especially helpful if you manage multiple locations or if
            nearby businesses have similar names. It ensures the QR code links to the correct page.
          </p>

          <h3 className="mt-10 text-xl font-bold tracking-tight text-ash-900" id="3-generate-qr-code" tabIndex={-1}>
            3. Generate Your QR Code
          </h3>
          <p className="mt-4">
            {" "}
            Hit the &quot;Generate QR Code&quot; button. The tool will instantly create a high-quality QR code
            that&apos;s easy to scan on mobile devices and works across different platforms.
          </p>

          <h3 className="mt-10 text-xl font-bold tracking-tight text-ash-900" id="4-download-and-share" tabIndex={-1}>
            4. Email and Share Your QR Code
          </h3>
          <p className="mt-4">
            You can email the QR code to yourself, your team, or your marketing team for immediate use. It&apos;s
            available in multiple formats like PNG, SVG, and PDF, making it perfect for both online and print purposes.
          </p>

          <p className="mt-6 text-xl leading-8">
            With your QR code ready, you&apos;re all set to start using it to boost your business visibility.
          </p>

          <h2
            className="mt-16 text-2xl font-bold tracking-tight text-ash-900"
            id="how-to-create-a-google-review-qr-code-for-your-business"
            tabIndex={-1}
          >
            How to Create a Google Review QR Code for Your Business
          </h2>
          <iframe
            className="mt-6 sb-iframe"
            src="https://www.youtube-nocookie.com/embed/Xc8_vxQz9Zg"
            loading="lazy"
            allowFullScreen
            style={{ width: "100%", height: "auto", aspectRatio: "16/9" }}
          ></iframe>

          <h2
            className="mt-16 text-2xl font-bold tracking-tight text-ash-900"
            id="advantages-of-our-qr-code-generator"
            tabIndex={-1}
          >
            Advantages of Our QR Code Generator
          </h2>
          <p className="mt-4">
            Our QR Code Generator is designed to help you connect with customers more effectively through your Google
            Business Profile.
          </p>

          <h3
            className="mt-10 text-xl font-bold tracking-tight text-ash-900"
            id="simplifies-customer-interaction"
            tabIndex={-1}
          >
            Simplifies Customer Interaction
          </h3>
          <p className="mt-4">
            With just one scan, customers can quickly{" "}
            <a
              href="https://rebolthq.com/tutorials/how-to-add-services-to-my-google-business-profile"
              className="text-rebolt-700"
            >
              access your Google Business Profile
            </a>{" "}
            to leave reviews, find contact information, check business hours, or request quotes. This saves them time
            and makes their experience more convenient. After a service, they can scan the QR code to provide immediate
            feedback or book their next appointment - no need for manual searches or extra steps.
          </p>

          <h3
            className="mt-10 text-xl font-bold tracking-tight text-ash-900"
            id="enhances-marketing-opportunities"
            tabIndex={-1}
          >
            Enhances Marketing Opportunities
          </h3>
          <p className="mt-4">
            Add QR codes to vehicles, business cards, invoices, or email signatures to turn everyday items into
            interactive marketing tools. For example, a plumber could include a QR code on service completion cards,
            making it easy for happy customers to leave reviews right away.
          </p>

          <h3
            className="mt-10 text-xl font-bold tracking-tight text-ash-900"
            id="delivers-a-professional-cost-effective-solution"
            tabIndex={-1}
          >
            Delivers a Professional, Cost-Effective Solution
          </h3>
          <p className="mt-4">
            Create high-quality QR codes instantly in various formats, suitable for both print and digital use. This
            affordable tool ensures your branding stays consistent across all materials. For businesses like home
            service providers, it connects on-site interactions with online engagement, creating a seamless customer
            experience that supports growth.
          </p>
          <p className="mt-6">
            Once your QR code is ready, placing it strategically can help you get the most out of it. Use it to create
            meaningful connections with your audience and drive results.
          </p>

          <h2
            className="mt-16 text-2xl font-bold tracking-tight text-ash-900"
            id="tips-for-using-your-qr-code-effectively"
            tabIndex={-1}
          >
            Tips for Using Your QR Code Effectively
          </h2>
          <p className="mt-4">
            Placing and using your{" "}
            <a href="https://tutorials.rebolthq.com/demo/cm13tz2pc0id01205m37wey0n" className="text-rebolt-700">
              Google Business Profile QR code
            </a>{" "}
            wisely can help increase customer engagement and make your business more visible. Here&apos;s how to get the
            most out of it across different platforms and track its success.
          </p>

          <h3
            className="mt-10 text-xl font-bold tracking-tight text-ash-900"
            id="where-to-place-qr-codes"
            tabIndex={-1}
          >
            Where to Place QR Codes
          </h3>
          <p className="mt-4">
            The location of your QR code plays a big role in how often it gets scanned. Put it in spots where people
            naturally pause, like checkout counters, waiting areas, or even on service vehicles. For service-based
            businesses, think about adding QR codes to invoices, business cards, or completion certificates - these are
            great opportunities for customers to leave reviews.
          </p>
          <p className="mt-4">
            Make sure your QR code is at least 2x2 cm, has a strong contrast against the background, includes enough
            white space, and works well in different lighting conditions.
          </p>

          <h3
            className="mt-10 text-xl font-bold tracking-tight text-ash-900"
            id="using-qr-codes-in-digital-campaigns"
            tabIndex={-1}
          >
            Using QR Codes in Digital Campaigns
          </h3>
          <p className="mt-4">
            QR codes can also be a powerful tool in your online marketing efforts. Add them to email signatures, social
            media posts, or digital newsletters to bridge the gap between your online and offline presence. This gives
            customers multiple ways to interact with your business.
          </p>
          <p className="mt-4">To make the most of digital QR codes:</p>
          <ul className="mt-6 list-disc pl-6 space-y-4">
            <li>Add a clear call-to-action alongside the code in emails, promotions, or other digital materials.</li>
            <li>Use dynamic QR codes so you can update the linked URL without needing a new code.</li>
            <li>
              Test the QR code to ensure it looks sharp and works across all screen sizes, from phones to desktops.
            </li>
          </ul>

          <h3
            className="mt-10 text-xl font-bold tracking-tight text-ash-900"
            id="tracking-qr-code-performance"
            tabIndex={-1}
          >
            Tracking QR Code Performance
          </h3>
          <p className="mt-4">
            Keeping an eye on how your QR code is performing will help you fine-tune its placement and usage. Use UTM
            parameters to track where traffic is coming from in{" "}
            <a href="https://marketingplatform.google.com/about/analytics/" target="_blank" className="text-rebolt-700">
              Google Analytics
            </a>
            . This helps you figure out which spots are driving the most scans.
          </p>
          <p className="mt-4">Key metrics to watch include:</p>
          <ul className="mt-6 list-disc pl-6 space-y-4">
            <li>The number of profile visits generated by QR code scans</li>
            <li>Times of day or days of the week with the most scans</li>
            <li>Conversion rates from scans to actions like reviews, calls, or website visits</li>
            <li>Customer feedback on how easy the QR code is to use</li>
          </ul>

          <h2
            className="mt-16 text-2xl font-bold tracking-tight text-ash-900"
            id="link-to-builtrights-google-reviews-qr-code-creator"
            tabIndex={-1}
          >
            Link to Rebolt&apos;s{" "}
            <a href="https://rebolthq.com/google-reviews-qr-code-creator" target="_blank" className="text-rebolt-700">
              Google Reviews QR Code Creator
            </a>
          </h2>
          <p className="mt-4">
            <Image
              src="/ReviewQRGen.png"
              alt="Google Reviews QR Code Creator"
              width={1000}
              height={1000}
              className="w-full"
            />
          </p>
          <p className="mt-4">
            The{" "}
            <a href="https://rebolthq.com/google-reviews-qr-code-creator" className="text-rebolt-700">
              Google Reviews QR Code Creator
            </a>{" "}
            is designed to make it simple for your customers to leave feedback. While the Google Business Profile QR
            Code Generator connects users to your business details, this tool focuses specifically on encouraging
            reviews. Considering that 90% of consumers check online reviews before visiting a business
            <a
              href="https://www.stanventures.com/blog/qr-code-for-google-review/"
              target="_blank"
              className="text-rebolt-700"
              style={{ textDecoration: "none" }}
            >
              <sup>[1]</sup>
            </a>
            , this can be a game-changer for boosting your local SEO and customer trust.
          </p>

          <h2 className="mt-16 text-2xl font-bold tracking-tight text-ash-900" id="conclusion" tabIndex={-1}>
            Conclusion
          </h2>
          <p className="mt-4">
            Once you&apos;ve applied these tips, it&apos;s time to consider how a QR code generator can reshape your
            business&apos;s approach to customer interaction. This tool seamlessly links your physical and digital
            presence, offering a fresh way to engage with customers in today&apos;s connected world.
          </p>
          <p className="mt-4">
            Here&apos;s the thing: customer engagement directly impacts results. With{" "}
            <strong>67% of consumers relying on reviews to make purchasing decisions</strong>
            <a
              href="https://www.qrcodechimp.com/qr-code-generator-for-googleReview"
              target="_blank"
              className="no-underline"
            >
              <sup>[3]</sup>
            </a>{" "}
            and businesses with <strong>82+ reviews generating 54% more revenue</strong>
            <a
              href="https://www.uniqode.com/blog/qr-code-for-reviews/google-reviews"
              target="_blank"
              className="no-underline"
            >
              <sup>[2]</sup>
            </a>
            , QR codes make it easier for customers to interact with your business. They work as ongoing, low-effort
            marketing tools that boost your online visibility and even help improve local SEO through increased
            engagement.
          </p>
          <p className="mt-4">
            For a more focused strategy, try pairing this tool with our{" "}
            <a href="https://rebolthq.com/google-reviews-qr-code-creator" className="text-rebolt-700">
              Google Reviews QR Code Creator
            </a>
            . This combo is especially useful for service-based businesses looking to expand their online presence and
            manage their reputation more effectively.
          </p>
          <p className="mt-4">
            Our QR code generator creates sleek, scannable codes that instantly connect your physical space to your
            Google Business Profile. You can use these codes in customer satisfaction surveys, on repair invoices, or in
            follow-up messages to maximize their impact. Start creating your custom QR code today and take advantage of
            this simple yet powerful way to grow your business.
          </p>

          <h2 className="mt-16 text-2xl font-bold tracking-tight text-ash-900" id="faqs" tabIndex={-1}>
            FAQs
          </h2>

          <h3
            className="mt-10 text-xl font-bold tracking-tight text-ash-900"
            id="where-should-qr-codes-be-displayed"
            tabIndex={-1}
          >
            Where should QR codes be displayed?
          </h3>

          <p className="mt-4">
            For home service businesses, placing your Google Business Profile QR code in the right spots can boost
            customer interaction. Here are some great options:
          </p>

          <ul className="mt-6 list-disc pl-6 space-y-4">
            <li>
              <strong>Service vehicles</strong>: Add QR codes to trucks or vans for visibility at job sites.
            </li>
            <li>
              <strong>Digital communications</strong>: Include them in follow-up emails, texts, or service reports.
            </li>
            <li>
              <strong>Business materials</strong>: Print them on invoices, business cards, and job estimates.
            </li>
            <li>
              <strong>Job site signage</strong>: Use temporary signs with QR codes during active projects.
            </li>
            <li>
              <strong>Service completion documents</strong>: Attach them to work orders or completion certificates.
            </li>
          </ul>

          <p className="mt-4">To make sure your QR codes are easy to scan:</p>
          <ul className="mt-6 list-disc pl-6 space-y-4">
            <li>Place them in well-lit areas.</li>
            <li>Ensure the size is at least 2x2 cm.</li>
            <li>Leave a clear border around the code.</li>
            <li>
              Add a simple call-to-action like <em>&quot;Scan to leave a review&quot;</em> or{" "}
              <em>&quot;Scan for business hours.&quot;</em>
            </li>
          </ul>

          <blockquote className="mt-6 border-l-4 border-rebolt-600 pl-4 italic">
            <p className="text-ash-900">
              &quot;Place QR codes where customers naturally pause, like checkout counters or service areas, to maximize
              engagement.&quot; - Sarah Johnson, Digital Marketing Strategist
            </p>
          </blockquote>

          <p className="mt-6">
            <strong>Pro Tip</strong>: Use dynamic QR codes so you can update the linked content without needing to
            reprint the code. Also, test them regularly to confirm they&apos;re working and directing users to your
            Google Business Profile.
          </p>
        </div>
      </div>

      <Footer />
    </>
  );
}
